import { Activity } from "lucide-react";
import type { EngineStatus, Health } from "@/hooks/useEngineInfo.ts";
import type { Metrics, ReadyEvent } from "@/lib/protocol.ts";

function ms(value: number | null | undefined) {
  return value == null ? "—" : value >= 1000 ? `${(value / 1000).toFixed(1)} s` : `${Math.round(value)} ms`;
}

export function SessionPanel({
  status,
  health,
  metrics,
  session,
  capture,
}: {
  status: EngineStatus;
  health: Health | null;
  metrics: Metrics | null;
  session: ReadyEvent | null;
  capture: { path: string; deviceRate: number; label: string } | null;
}) {
  const p = health?.provider;
  const engineText =
    status === "down"
      ? "Engine offline"
      : status === "degraded"
        ? p?.reachable
          ? `Model ${p.model ?? ""} not found on host`
          : "Model host unreachable"
        : status === "ok"
          ? `${p?.model ?? "Gemma 4"} via ${p?.api ?? "host"}`
          : "Checking engine…";
  const dot = status === "ok" ? "bg-ok" : status === "unknown" ? "bg-faint" : "bg-danger";

  const rows: Array<[string, string]> = [
    ["First words", ms(metrics?.firstTextP50Ms)],
    ["Final after pause", `${ms(metrics?.finalP50Ms)} · p95 ${ms(metrics?.finalP95Ms)}`],
    ["Backlog", metrics ? `${(metrics.lagS ?? 0).toFixed(1)} s audio` : "—"],
    ["Model reads", metrics ? `${metrics.partials ?? 0} previews · ${metrics.finals ?? 0} finals · ${metrics.refines ?? 0} refines` : "—"],
    ["Corrections", metrics ? String(metrics.revisions ?? 0) : "—"],
  ];

  return (
    <section className="card p-4" aria-labelledby="session-title">
      <h2 id="session-title" className="flex items-center gap-2 text-sm font-medium">
        <Activity size={16} className="text-accent" /> Engine
      </h2>
      <p className="mt-2 flex items-center gap-2 text-sm">
        <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden="true" />
        <span className="truncate">{session?.simulated ? "Simulated demo (no model)" : engineText}</span>
      </p>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted">{k}</dt>
            <dd className="text-right tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
      {capture ? (
        <p className="mt-3 truncate text-xs text-faint" title={capture.label}>
          Mic: {capture.label || "default"} · {capture.deviceRate / 1000} kHz {capture.path === "resampled" ? "→ 16 kHz" : ""}
        </p>
      ) : null}
      {health?.retention ? <p className="mt-2 text-xs text-faint">{health.retention}</p> : null}
    </section>
  );
}
